/**
 * imageCache.ts
 *
 * Module-level in-memory registry of image URLs that have successfully loaded
 * at least once during the current browser session. Survives SPA navigation
 * (Next.js client-side routing) but resets on a full-page reload — at that
 * point the browser's HTTP cache takes over anyway.
 *
 * Using this registry lets components skip the loading-skeleton for URLs they
 * already know to be valid, eliminating the "flash of empty" on back-navigation.
 */

const _loaded = new Set<string>();

/** Returns true when the given URL has previously loaded successfully. */
export function isImageCached(url: string | undefined | null): boolean {
    return !!url && _loaded.has(url);
}

/** Mark a URL as confirmed-good (called from an img's onLoad handler). */
export function markImageLoaded(url: string | undefined | null): void {
    if (url) _loaded.add(url);
}

/**
 * Eagerly preload an array of URLs so they land in the browser's HTTP cache
 * before the images are painted. Safe to call with duplicates or already-cached
 * URLs — those are no-ops.
 *
 * Call this after your data list is available (e.g. in a useEffect that
 * depends on the events array) so that when the cards first render the images
 * are already warm in cache.
 */
export function preloadImages(urls: (string | undefined | null)[]): void {
    if (typeof window === "undefined") return;
    urls.forEach((url) => {
        if (!url || _loaded.has(url)) return;
        const img = new window.Image();
        img.onload = () => _loaded.add(url);
        // onError intentionally omitted — broken URLs stay out of the set
        img.src = url;
    });
}
